import './SubscriptionDetails.css';

import {
  Input,
  Form,
  Select,
  Card,
  Button,
  DatePicker,
  Popconfirm,
  Alert,
  message,
  Flex,
  InputNumber,
  Switch,
  Popover,
  Modal,
  Typography,
} from 'antd';
import Subscription from '../../../domain/models/Subscription';
import SubscriptionOpeningDays, {
  subscriptionOpeningDaysAsString,
} from '../../../domain/enums/subscription/subscription_opening/SubscriptionOpeningDays';
import SubscriptionTimezoneCity, {
  subscriptionTimezoneCityAsString,
} from '../../../domain/enums/subscription/subscription_timezone/SubscriptionTimezoneCity';
import SubscriptionTimezoneContinent, {
  subscriptionTimezoneContinentAsString,
} from '../../../domain/enums/subscription/subscription_timezone/SubscriptionTimezoneContinent';
import SubscriptionTimezoneCountry, {
  subscriptionTimezoneCountryAsString,
} from '../../../domain/enums/subscription/subscription_timezone/SubscriptionTimezoneCountry';
import { useCallback, useContext, useEffect, useState } from 'react';
import SubscriptionEditionFormContext from '../../contexts/forms/subscriptions/SubscriptionEditionFormContext';
import PlansContext from '../../contexts/data/PlansContext';
import AuthenticationContext from '../../contexts/auth/AuthenticationContext';
import SMSContext from '../../contexts/data/SMSContext';
import MultiGridInput from '../@shared/MultiGridInput';
import { SUBSCRIPTIONS_PAGE_PATH } from '../../pages/subscriptions/SubscriptionsPage';
import moment from 'moment';
import dayjs, { Dayjs } from 'dayjs';
import AIContext from '../../contexts/data/AIContext';
import SubscriptionsContext from '../../contexts/data/SubscriptionsContext';
import { CheckCircleFilled, CheckCircleOutlined, CloseCircleFilled, CreditCardOutlined, DeleteOutlined, EditOutlined, GithubOutlined, HourglassOutlined, MailOutlined, PlusOutlined, SendOutlined, SwapOutlined, TransactionOutlined, VideoCameraAddOutlined } from '@ant-design/icons';
import NumbersContext from '../../contexts/data/NumbersContext';
import { FormCard, FormContainer, FormLabelWithHelp, FormSectionTitle, READ_ONLY_INPUT_STYLE, EmailList, CustomSelect } from '@leedpulse-monorepo/components';
import {
  getSenderCountryFlag,
} from '@leedpulse-monorepo/utils';
import { creditTypeAsString, EProductType, UserRole, ContextStatus, ECreditType, channelAsString, SubscriptionStatus } from '@leedpulse-monorepo/enums';
import UsersContext from '../../contexts/data/UsersContext';
import TerminateSubscriptionPopconfirm from './TerminateSubscriptionPopconfirm';
import SubscriptionDataSource, { subscriptionDataSourceAsString } from '../../../domain/enums/subscription/subscription_datasource/SubscriptionDataSource';
import { useNavigate } from 'react-router-dom';
import { NUMBERS_PAGE_PATH } from '../../pages/numbers/NumbersPage';
import DomainsContext from '../../contexts/data/DomainsContext';
import getPlanById from '../../../domain/use_cases/plans/getPlanById';
import { IPlan } from '@leedpulse-monorepo/interfaces';
import deleteDemoAccount from '../../../domain/use_cases/demo/deleteDemoAccount';
import DebouncedPlansSelect from '../plans_page/select/DebouncedPlansSelect';
import { FirestoreCollections } from '@leedpulse-monorepo/firebase-metadata';
import MigrateSenderModal from './MigrateSenderModal';
import CreditsAdjustmentModal, { CreditsAdjustmentMode } from './CreditsAdjustmentModal';
import ChangePlanModal, { changePlanTargetIds } from './ChangePlanModal';
import LeedbucksRatesList from '../@shared/leedbucks/LeedbucksRatesList/LeedbucksRatesList';
import LeedbucksRatesModalButton from '../@shared/leedbucks/LeedbucksRatesModalButton/LeedbucksRatesModalButton';
import LeedbucksTempTabs from '../@shared/leedbucks/LeedbucksTempTabs/LeedbucksTempTabs';
import isEqual from 'lodash/isEqual';

interface SubscriptionDetailProps {
  subscription: Subscription;
  companyId?: string; // Owning company. Required to change the plan or cancel a scheduled downsell, the endpoints key on it
  billingPeriodEnd?: number | null; // Company Stripe period end, not the credits cycle
}

const { Option } = Select;

const SubscriptionDetails: React.FC<SubscriptionDetailProps> = ({
  subscription,
  companyId,
  billingPeriodEnd,
}) => {
  const navigate = useNavigate();
  const formContext = useContext(SubscriptionEditionFormContext);
  const plansContext = useContext(PlansContext);
  const authContext = useContext(AuthenticationContext);
  const smsContext = useContext(SMSContext);
  const usersContext = useContext(UsersContext);
  const domainsContext = useContext(DomainsContext);

  const aiContext = useContext(AIContext);

  const subscriptionsContext = useContext(SubscriptionsContext);
  const numbersContext = useContext(NumbersContext);

  const [availableNetworks, setAvailableNetworks] = useState<string[]>([]);
  const [subPlan, setSubPlan] = useState<IPlan | null>(null);
  const [demoDeleteModalOpen, setDemoDeleteModalOpen] = useState(false);
  const [isDeletingDemoAccount, setIsDeletingDemoAccount] = useState(false);
  const [senderToMigrate, setSenderToMigrate] = useState<string | null>(null);
  const [anchorDay, setAnchorDay] = useState<number | null>(null);
  const [hasMigratedSender, setHasMigratedSender] = useState(false);
  const [creditsModal, setCreditsModal] = useState<CreditsAdjustmentMode | null>(null);
  const [isChangePlanOpen, setIsChangePlanOpen] = useState(false);

  useEffect(() => {
    smsContext.fetchLatestSMS(subscription);

    if (plansContext.status === ContextStatus.IDLE) {
      plansContext.fetch();
    }

    // Not gated on the shared status: another page can leave it in SUCCESS without this domain
    domainsContext.fetchBySubscription(subscription.id).then((domain) => {
      setAvailableNetworks(domain?.networks || []);
    });

    usersContext.fetchRoles([UserRole.admin, UserRole.maintainer, UserRole.owner]);

    if (formContext.wipSubscription === null) {
      formContext.initWithSubscription(subscription);
    }
  }, []);

  useEffect(() => {
    if (!formContext.wipSubscription?.idPlan) {
      // Synchro avec une source distante : l'etat vient d'un appel reseau, pas du
      // rendu. C'est l'usage d'un effet que la regle autorise ("synchronizing with an
      // external system") ; le setState synchrone est le reset qui precede l'appel.
      // eslint-disable-next-line react/set-state-in-effect
      setSubPlan(null);
      return;
    }
    getPlanById(formContext.wipSubscription.idPlan).then(setSubPlan);
  }, [formContext.wipSubscription?.idPlan]);

  useEffect(() => {
    formContext.fetchPartnerUserUuids();

    if (formContext.wipSubscription) {
      numbersContext.fetchFromSenders(formContext.wipSubscription.sender);

      if (formContext.updateStatus !== ContextStatus.IDLE) {
        formContext.resetUpdateStatus();
      }
    }
  }, [formContext.wipSubscription]);

  useEffect(() => {
    switch (formContext.updateStatus) {
      case ContextStatus.SUCCESS:
        message.success("Mise à jour de l'abonnement avec succès");
        break;
      case ContextStatus.ERROR:
        message.error("Erreur lors de la mise à jour de l'abonnement");
        break;
      default:
        break;
    }
  }, [formContext.updateStatus]);

  useEffect(() => {
    switch (formContext.deleteStatus) {
      case ContextStatus.SUCCESS:
        message.success('Abonnement supprimé avec succès');
        navigate(SUBSCRIPTIONS_PAGE_PATH);
        break;
      case ContextStatus.ERROR:
        message.error("Erreur lors de la suppression de l'abonnement");
        break;
      default:
        break;
    }
  }, [formContext.deleteStatus]);

  const isBusy =
    formContext.updateStatus === ContextStatus.LOADING ||
    formContext.deleteStatus === ContextStatus.LOADING ||
    isDeletingDemoAccount;

  const isReadOnly =
    authContext.authentication == null ||
    ![UserRole.owner, UserRole.admin, UserRole.maintainer].includes(
      authContext.authentication?.role
    );
  // Editing the balance and resetting it are owner-only, any staff can add credits
  const isOwner = authContext.authentication?.role === UserRole.owner;
  // Promotions too, credits and temporary rates: the others see them read-only
  const isPromoReadOnly = isReadOnly || !isOwner;

  const wipSubscription = formContext.wipSubscription;

  // Moving plan_end opens or cuts the API access
  // The day the credits land on. Stripe-billed subscriptions take it from their cycle, so the
  // control only exists where `plan_start` is the anchor: a Company with no billing mirror.
  const isStripeAnchored = !!billingPeriodEnd;
  const currentAnchorDay = wipSubscription?.planStart?.date() ?? null;

  const confirmAnchorDayChange = () => {
    if (anchorDay === null || anchorDay === currentAnchorDay) return;

    Modal.confirm({
      title: `Déplacer la recharge au ${anchorDay} de chaque mois ?`,
      content: (
        <Flex vertical gap={4}>
          <span>Le changement est pris en compte par la recharge de nuit, à 23h.</span>
          <span>• si le cycle qui s&apos;ouvre au nouveau jour n&apos;a pas encore été servi, les crédits tombent ce soir ;</span>
          <span>• sinon rien n&apos;est accordé, la recharge suivante tombe au nouveau jour ;</span>
          <span>• déplace aussi le début d&apos;abonnement, qui porte l&apos;ancre ;</span>
          <span>• sur un mois plus court, la recharge tombe le dernier jour.</span>
        </Flex>
      ),
      okText: 'Déplacer',
      cancelText: 'Annuler',
      onOk: () => formContext.onCreditsAnchorDayChange(anchorDay),
      onCancel: () => setAnchorDay(null),
    });
  };

  const confirmPlanEndChange = (date: Dayjs | null) => {
    if (!date) return;

    Modal.confirm({
      title: "Déplacer la date d'accès garanti ?",
      content: (
        <Flex vertical gap={4}>
          <span>Cette date est le droit d&apos;accès, pas une date de facturation. La déplacer :</span>
          <span>• ouvre ou coupe l&apos;accès à l&apos;API au jour dit ;</span>
          <span>• ne résilie rien et ne touche pas à l&apos;abonnement Stripe ;</span>
          <span>• une fois dépassée, arrête la recharge des crédits et libère le numéro.</span>
        </Flex>
      ),
      okText: 'Déplacer',
      cancelText: 'Annuler',
      onOk: () => formContext.onPlanEndChange(date),
    });
  };

  // Downsell scheduled by the Leedflow Stripe flow — read-only here
  const pendingPlanChange = subscriptionsContext.selected?.pendingPlanChange;
  // Plan change set for a later day from here, run by the 4am job
  const scheduledPlanChange = subscriptionsContext.selected?.scheduledPlanChange ?? null;
  const planName = (id: string) => plansContext.items.find((plan) => plan.id === id)?.name ?? id;

  // Recursive function to check if a field has changed
  const isFieldChanged = useCallback((field: string): boolean => {
    if (!wipSubscription || formContext.updateStatus !== ContextStatus.IDLE) {
      return false;
    }

    const getNestedValue = (obj: unknown, keys: string[]): unknown => {
      if (keys.length === 0 || obj === null || obj === undefined) {
        return obj;
      }
      const [firstKey, ...restKeys] = keys;
      return getNestedValue((obj as Record<string, unknown>)[firstKey], restKeys);
    };

    const keys = field.split('.');
    const wipValue = getNestedValue(wipSubscription, keys);
    const selectedValue = getNestedValue(subscriptionsContext.selected, keys);

    return wipValue !== selectedValue;
  }, [wipSubscription, subscriptionsContext.selected, formContext.updateStatus]);

  const isLeedflowDemo =
    Boolean(wipSubscription?.settings?.demo_account) &&
    subPlan?.product === EProductType.LEEDFLOW;

  // Leedflow terminations always go through Stripe (end of the billing period),
  // so no termination date is asked for them.
  const isLeedflow = subPlan?.product === EProductType.LEEDFLOW;

  const isAlreadyTerminating =
    wipSubscription?.status === SubscriptionStatus.CANCELLING ||
    wipSubscription?.status === SubscriptionStatus.CANCELLED;

  // The plans the current one leads to, applied on Stripe: only for a number Stripe bills. Not
  // while the plan field is edited, the lists would be the unsaved plan's.
  const canChangePlan = !isReadOnly && isLeedflow && isStripeAnchored && !!companyId && subPlan !== null &&
    wipSubscription?.status !== SubscriptionStatus.CANCELLED &&
    wipSubscription?.status !== SubscriptionStatus.PAUSED &&
    wipSubscription?.idPlan === subscriptionsContext.selected?.idPlan &&
    changePlanTargetIds(subPlan, plansContext.items).length > 0;

  const handleRequestDelete = async () => {
    if (isLeedflowDemo) {
      if (!wipSubscription || !authContext.authentication) return;
      setIsDeletingDemoAccount(true);
      try {
        await deleteDemoAccount(wipSubscription.id, authContext.authentication);
        setDemoDeleteModalOpen(false);
        message.success('Compte de démonstration supprimé (données + utilisateur).');
        navigate(SUBSCRIPTIONS_PAGE_PATH);
      } catch (e) {
        message.error(
          e instanceof Error
            ? e.message
            : "Erreur lors de la suppression du compte de démonstration"
        );
      } finally {
        setIsDeletingDemoAccount(false);
      }
    } else {
      void formContext.submitDelete();
    }
  };

  return wipSubscription === null ? (
    <Card loading />
  ) : (
    <FormCard
      title={wipSubscription.id}
      collectionName={FirestoreCollections.subscriptions}
      onDeleted={isReadOnly ? undefined : () => handleRequestDelete()}
      onUpdated={isReadOnly ? undefined : () => formContext.submitUpdate()}
      isUpdateLoading={isBusy}
      isDeleteLoading={isBusy}
      headerActions={
        isReadOnly || isAlreadyTerminating ? undefined : [
          <TerminateSubscriptionPopconfirm
            key="terminate-popconfirm"
            formContext={formContext}
            disabled={isBusy}
            isLoading={formContext.updateStatus === ContextStatus.LOADING}
            isLeedflow={isLeedflow}
          />
        ]
      }
    >
      <Form
        layout="vertical"
        initialValues={{ remember: true }}
        requiredMark={false}
      >
        <Form.Item label="Notes">
          <Input.TextArea
            value={wipSubscription.notes}
            onChange={(e) => formContext.onNotesChange(e.target.value)}
            placeholder="Notes internes sur cet abonnement (conditions particulières, remises, échéances...)"
            rows={3}
          />
        </Form.Item>

        <FormSectionTitle title="Informations générales" />
        {smsContext.latestSms?.length > 0 ? (
          <Alert
            message={<Flex align="center"><CheckCircleOutlined style={{ marginRight: 6 }} />Automatisation démarrée</Flex>}
            description={
              'Le SMS le plus ancien a été envoyé le ' +
              moment(smsContext.latestSms[0].created).format('LLLL')
            }
            type="success"
            style={{ marginBottom: '24px' }}
          />
        ) : (
          <Alert
            message={<Flex align="center"><HourglassOutlined style={{ marginRight: 6 }} />Automatisation non démarrée</Flex>}
            description={"Aucun SMS n'a pour le moment été envoyé"}
            type="warning"
            style={{ marginBottom: '24px' }}
          />
        )}
        {pendingPlanChange && (
          <Alert
            type="info"
            showIcon
            message="Downsell planifié"
            description={
              `Passage au plan ${plansContext.items.find((plan) => plan.id === pendingPlanChange.id_plan)?.name ?? pendingPlanChange.id_plan} ` +
              `le ${moment(pendingPlanChange.effective_at).format('LL')} (prochaine facturation). ` +
              `Demandé le ${moment(pendingPlanChange.requested_at).format('LL')}. ` +
              `Le prix Stripe est déjà positionné, le plan interne bascule à la facturation.`
            }
            action={!isReadOnly && companyId ? (
              <Popconfirm
                title="Annuler ce downsell ? Le prix Stripe est remis sur le plan en cours, sans ligne de prorata."
                onConfirm={async () => {
                  await formContext.onCancelPlanChange(companyId);
                  subscriptionsContext.fetchAndSelectById(subscription.id);
                }}
                okText="Annuler le downsell"
                cancelText="Fermer"
              >
                <Button size="small" danger loading={isBusy}>Annuler</Button>
              </Popconfirm>
            ) : undefined}
            style={{ marginBottom: '24px' }}
          />
        )}
        {scheduledPlanChange && (
          <Alert
            type={scheduledPlanChange.last_error ? 'warning' : 'info'}
            showIcon
            message="Changement de plan planifié"
            description={
              `Passage au plan ${planName(scheduledPlanChange.id_plan)} le ${moment(scheduledPlanChange.scheduled_at).format('LL')}` +
              (scheduledPlanChange.change_type === 'upgrade'
                ? ', prorata facturé ce jour-là. '
                : ` : prix Stripe basculé d'ici là, plan basculé à l'échéance qui suit. `) +
              `Demandé le ${moment(scheduledPlanChange.requested_at).format('LL')}.` +
              (scheduledPlanChange.last_error ? ` Dernier essai en échec, nouvel essai la nuit prochaine : ${scheduledPlanChange.last_error}` : '')
            }
            action={!isReadOnly ? (
              <Popconfirm
                title="Annuler ce changement planifié ? Rien n'a encore bougé sur Stripe."
                onConfirm={async () => {
                  await formContext.onCancelScheduledPlanChange();
                  subscriptionsContext.fetchAndSelectById(subscription.id);
                }}
                okText="Annuler le changement"
                cancelText="Fermer"
              >
                <Button size="small" danger loading={isBusy}>Annuler</Button>
              </Popconfirm>
            ) : undefined}
            style={{ marginBottom: '24px' }}
          />
        )}
        <FormContainer>
          <Form.Item label="Clé API">
            <Input
              type="text"
              disabled={true}
              readOnly={true}
              style={READ_ONLY_INPUT_STYLE}
              value={subscriptionsContext.selected?.apiKey}
            />
          </Form.Item>
        </FormContainer>
        {wipSubscription.partnerUid && (
          <FormContainer twoColumnsMax>
            <Form.Item label="Identifiant partenaire">
              <Input
                type="text"
                disabled={true}
                readOnly={true}
                style={READ_ONLY_INPUT_STYLE}
                value={wipSubscription.partnerUid + ' (' + wipSubscription.partnerSystem + ')'}
              />
            </Form.Item>
            <Form.Item label="Clé API partenaire">
              <Input
                type="text"
                disabled={true}
                readOnly={true}
                style={READ_ONLY_INPUT_STYLE}
                value={wipSubscription.partnerApiKey}
              />
            </Form.Item>
          </FormContainer>
        )}
        <FormContainer>
          <Form.Item label="Nom">
            <Input
              type="text"
              disabled={isBusy}
              readOnly={isReadOnly}
              style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
              onChange={(e) => formContext.onNameChange(e.target.value)}
              value={wipSubscription.name}
              className={isFieldChanged("name") ? "changed" : ""}
            />
          </Form.Item>
          <Form.Item label={
            <Flex align="center" gap={4}>
              Crédits
              {isOwner && (
                <Popover content="Provoquer un renouvellement des crédits">
                  <Button
                    className="subscription-details__label-action"
                    type="text"
                    shape="circle"
                    size="small"
                    icon={<TransactionOutlined />}
                    disabled={isBusy}
                    onClick={() => setCreditsModal('reset')}
                  />
                </Popover>
              )}
            </Flex>
          }>
            <Flex gap={8}>
              {Object.keys(ECreditType)
                .filter((key) => wipSubscription.credits[key as ECreditType] !== undefined)
                .map((key) => {
                  // On a Leedflow sub the legacy sms/minutes/contacts buckets are unwanted → error + clearable.
                  const isLegacy = subPlan?.product === EProductType.LEEDFLOW && key !== ECreditType.leedbucks;
                  // The leedbucks balance only moves through the traced actions next to it
                  const isLeedbucks = key === ECreditType.leedbucks;
                  return (
                    <Flex key={key} gap={4} align="center">
                      <Popover content={creditTypeAsString(key, false)}>
                        <span>{creditTypeAsString(key)}</span>
                      </Popover>
                      <InputNumber
                        disabled={isBusy}
                        readOnly={isReadOnly || isLeedbucks}
                        status={isLegacy ? 'error' : undefined}
                        style={isReadOnly || isLeedbucks ? READ_ONLY_INPUT_STYLE : { width: '100%' }}
                        onChange={(value) =>
                          formContext.onCreditsChange(key as ECreditType, value ?? 0)
                        }
                        value={wipSubscription.credits[key as ECreditType]}
                        step={1}
                        className={!isLeedbucks && isFieldChanged(`credits.${key}`) ? "changed" : ""}
                      />
                      {isLeedbucks && isLeedflow && !isReadOnly && (
                        <Popover content="Ajouter des crédits">
                          <Button
                            size="small"
                            icon={<PlusOutlined />}
                            disabled={isBusy}
                            onClick={() => setCreditsModal('add')}
                          />
                        </Popover>
                      )}
                      {isLeedbucks && isLeedflow && isOwner && (
                        <Popover content="Modifier le solde">
                          <Button
                            size="small"
                            icon={<EditOutlined />}
                            disabled={isBusy}
                            onClick={() => setCreditsModal('set')}
                          />
                        </Popover>
                      )}
                      {isLegacy && !isReadOnly && (
                        <Button
                          danger
                          size="small"
                          icon={<DeleteOutlined />}
                          disabled={isBusy}
                          title="Supprimer ce crédit legacy (non utilisé par Leedflow)"
                          onClick={() => formContext.onClearCredit(key as ECreditType)}
                        />
                      )}
                    </Flex>
                  );
                })}
            </Flex>
          </Form.Item>
          <Form.Item label='Employé en charge du client'>
            <Select
              disabled={isBusy || isReadOnly || !wipSubscription.employeeInChargeUid || usersContext?.items?.length <= 0}
              placeholder="Sélectionner une personne..."
              value={wipSubscription.employeeInChargeUid}
              onChange={(value) => {
                formContext.onEmployeeInChargeChange(value)
              }}
              className={isFieldChanged("employeeInChargeUid") ? "changed" : ""}
            >
              {
                usersContext?.items.map((user) => (
                  <Option key={user.uid} value={user.uid}>
                    {user.firstname + ' ' + user.lastname}
                  </Option>
                ))
              }
            </Select>
          </Form.Item>
          <Form.Item
            label={!isLeedflow ? 'Plan' : <FormLabelWithHelp label="Plan" help="Le champ change le plan Leedflow seul, sans toucher à Stripe : réservé aux owners. Pour un numéro facturé par Stripe, passer par Changer, qui applique le changement sur Stripe maintenant ou à une date." />}
            required
            initialValue={wipSubscription.idPlan}
          >
            <Flex gap={8}>
              <DebouncedPlansSelect
                auth={authContext.authentication}
                showSearch
                customOptions={false}
                placeholder="Rechercher un plan..."
                style={{ flex: 1, minWidth: 0 }}
                disabled={isBusy || isReadOnly || (!isOwner && isLeedflow)}
                value={wipSubscription.idPlan}
                onChange={(value) => formContext.onIdPlanChange(value as string)}
                className={isFieldChanged("idPlan") ? "changed" : ""}
              />
              {canChangePlan && (
                <Button icon={<SwapOutlined />} disabled={isBusy} onClick={() => setIsChangePlanOpen(true)}>
                  Changer
                </Button>
              )}
            </Flex>
          </Form.Item>
          <Form.Item
            label="Jours d'ouverture"
            initialValue={wipSubscription.opening.days}
          >
            <Select
              style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
              disabled={isBusy || isReadOnly}
              onChange={(value) =>
                formContext.onOpeningDaysChange(
                  value as SubscriptionOpeningDays
                )
              }
              value={wipSubscription.opening.days}
              className={isFieldChanged("opening.days") ? "changed" : ""}
            >
              {Object.keys(SubscriptionOpeningDays).map((key) => (
                <Select.Option
                  key={key}
                  value={
                    SubscriptionOpeningDays[
                    key as keyof typeof SubscriptionOpeningDays
                    ]
                  }
                >
                  {subscriptionOpeningDaysAsString(
                    SubscriptionOpeningDays[
                    key as keyof typeof SubscriptionOpeningDays
                    ]
                  )}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Flex align="center" justify="space-between" gap={8}>
            <Form.Item label="Début d'abonnement" style={{ width: '100%' }}>
              <DatePicker
                style={{ width: '100%' }}
                disabled={true}
                format={'DD/MM/YYYY'}
                onChange={(date) => formContext.onPlanStartChange(date)}
                value={wipSubscription.planStart}
                allowClear={false}
                className={isFieldChanged("planStart") ? "changed" : ""}
              />
            </Form.Item>
            <Form.Item
              label={<FormLabelWithHelp label="Accès garanti jusqu'au" help="Droit d'accès, pas une date de facturation. Sur Leedflow : fin de période Stripe + 1 mois de marge. Ne résilie rien, coupe juste l'accès à l'API." />}
              style={{ width: '100%' }}
            >
              <DatePicker
                style={{ width: '100%' }}
                disabled={isBusy || isReadOnly}
                format={'DD/MM/YYYY'}
                onChange={confirmPlanEndChange}
                value={wipSubscription.planEnd}
                allowClear={false}
                className={isFieldChanged("planEnd") ? "changed" : ""}
              />
            </Form.Item>
          </Flex>
          <Flex align="center" justify="space-between" gap={8}>
            <Form.Item label="Heure d'ouverture" style={{ width: '100%' }}>
              <Input
                type="number"
                disabled={isBusy}
                readOnly={isReadOnly}
                style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
                onChange={(e) =>
                  formContext.onOpeningHoursStartChange(Number(e.target.value))
                }
                value={wipSubscription.opening.hours.start}
                min={0}
                className={isFieldChanged("opening.hours.start") ? "changed" : ""}
              />
            </Form.Item>
            <Form.Item label="Heure de fermeture" style={{ width: '100%' }}>
              <Input
                type="number"
                disabled={isBusy}
                readOnly={isReadOnly}
                style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
                onChange={(e) =>
                  formContext.onOpeningHoursEndChange(Number(e.target.value))
                }
                value={wipSubscription.opening.hours.end}
                max={24}
                className={isFieldChanged("opening.hours.end") ? "changed" : ""}
              />
            </Form.Item>
          </Flex>

          <Form.Item label="Source des données (CRM)">
            <Select
              showSearch
              placeholder="Sélectionner une source de données..."
              value={wipSubscription.datasource}
              onChange={(value) => {
                formContext.onDataSourceChange(value as SubscriptionDataSource)
              }}
              className={isFieldChanged("datasource") ? "changed" : ""}
            >
              {
                Object.values(SubscriptionDataSource).map((datasource) => (
                  <Option key={datasource} value={datasource}>
                    {subscriptionDataSourceAsString(datasource)}
                  </Option>
                ))
              }
            </Select>
          </Form.Item>

          <Form.Item label="Ecran de paiement">
            <Select
              disabled={isBusy || isReadOnly}
              style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
              onChange={(value) => {
                formContext.onSubscriptionSettingsPaymentWallChange(
                  value === 'true'
                );
              }}
              value={
                wipSubscription?.settings?.payment_wall !== undefined
                  ? wipSubscription.settings.payment_wall.toString()
                  : 'false'
              }
              className={isFieldChanged("settings.payment_wall") ? "changed" : ""}
            >
              <Option value="true"><CheckCircleFilled style={{ color: 'green', marginRight: '8px' }} />Oui</Option>
              <Option value="false"><CloseCircleFilled style={{ color: 'red', marginRight: '8px' }} />Non</Option>
            </Select>
          </Form.Item>

          <Form.Item label="Réseau">
            {/* <Input
              disabled={isBusy}
              onChange={(e) => {
                formContext.onNetworkChange(e.target.value);
              }}
              value={wipSubscription.network}
            /> */}
            <CustomSelect
              options={availableNetworks.map((network) => ({
                label: network,
                value: network,
              }))}
              defaultValue={wipSubscription.network}
              onChange={(value) => {
                formContext.onNetworkChange(value as string);
              }}
              placeholder="Ex: Century 21"
              disabled={isBusy}
              loading={domainsContext.status === ContextStatus.LOADING}
              allowClear
            />
          </Form.Item>

          {isLeedflow &&
              <Form.Item label="Taux Leedbucks">
                <Flex gap={8} wrap="wrap">
                  <LeedbucksRatesModalButton
                    label="Configurer les taux"
                    title="Taux Leedbucks de l'abonnement"
                    isModified={!isEqual(wipSubscription.leedbucksRates ?? {}, formContext.savedSubscription?.leedbucksRates ?? {})}
                  >
                    <LeedbucksRatesList
                      rates={wipSubscription.leedbucksRates ?? {}}
                      onChange={(rates) => formContext.onLeedbucksRatesChange(rates)}
                      isReadOnly={isReadOnly}
                      canManageRows
                    />
                  </LeedbucksRatesModalButton>
                  <LeedbucksRatesModalButton
                    label="Configurer les taux temporaires"
                    title="Taux Leedbucks temporaires de l'abonnement"
                    isModified={!isEqual(wipSubscription.leedbucksRatesTemp ?? [], formContext.savedSubscription?.leedbucksRatesTemp ?? [])}
                  >
                    <LeedbucksTempTabs
                      periods={wipSubscription.leedbucksRatesTemp ?? []}
                      onChange={(periods) => formContext.onLeedbucksRatesTempChange(periods)}
                      isReadOnly={isPromoReadOnly}
                      createPeriod={(id, ending_at) => ({ id, ending_at, rates: {} })}
                      renderPeriod={(period, update) => <LeedbucksRatesList
                        rates={period.rates}
                        onChange={(rates) => update({ rates })}
                        isReadOnly={isPromoReadOnly}
                        canManageRows
                      />}
                      emptyLabel="Aucun taux temporaire"
                    />
                  </LeedbucksRatesModalButton>
                  <LeedbucksRatesModalButton
                    label="Configurer les promotions"
                    title="Crédits promotionnels de l'abonnement"
                    isModified={!isEqual(wipSubscription.leedbucksCreditsTemp ?? [], formContext.savedSubscription?.leedbucksCreditsTemp ?? [])}
                  >
                    <Typography.Text type="secondary">
                      Tant qu'une période court, chaque recharge donne ses crédits à la place de ceux du plan, celle qui finit le plus tôt d'abord. Elle s'applique à la prochaine recharge.
                    </Typography.Text>
                    <LeedbucksTempTabs
                      periods={wipSubscription.leedbucksCreditsTemp ?? []}
                      onChange={(periods) => formContext.onLeedbucksCreditsTempChange(periods)}
                      isReadOnly={isPromoReadOnly}
                      // Starts at the plan's allocation: one saved without a value still grants the usual credits
                      createPeriod={(id, ending_at) => ({ id, ending_at, leedbucks: subPlan?.credits?.leedbucks ?? 0 })}
                      renderPeriod={(period, update) => <Form.Item label="Crédits par recharge" style={{ marginBottom: 0 }}>
                        <InputNumber
                          min={0}
                          precision={0}
                          value={period.leedbucks}
                          onChange={(leedbucks) => update({ leedbucks: leedbucks ?? 0 })}
                          disabled={isPromoReadOnly}
                          addonAfter="leedbucks"
                        />
                      </Form.Item>}
                      emptyLabel="Aucune promotion"
                    />
                  </LeedbucksRatesModalButton>
                </Flex>
              </Form.Item>
            }
        </FormContainer>
        <FormSectionTitle title="Cycle" />
        <FormContainer>
          <Form.Item label={<FormLabelWithHelp label="Cycle en cours" help="Début du cycle de crédits en cours. Écrit par la recharge de nuit." />}>
            <DatePicker
              style={{ ...READ_ONLY_INPUT_STYLE, width: '100%', pointerEvents: 'none', cursor: 'not-allowed' }}
              readOnly
              format="DD/MM/YYYY HH:mm"
              value={wipSubscription.creditsResetAt ? dayjs(wipSubscription.creditsResetAt) : null}
              placeholder="Aucun"
            />
          </Form.Item>
          <Form.Item label={<FormLabelWithHelp label="Fin d'engagement" help="Douze mois de service, portés par ce numéro. À la résiliation, la coupure tombe à la fin de la première période qui se termine après cette date, et la ligne Stripe reste facturée jusque-là. Vide : aucun engagement. Poser une date d'onboarding sur la société la déplace." />}>
            <DatePicker
              style={{ width: '100%' }}
              disabled={isBusy || isReadOnly}
              format="DD/MM/YYYY"
              value={wipSubscription.commitmentDate ? dayjs(wipSubscription.commitmentDate) : null}
              onChange={(date) => formContext.onCommitmentDateChange(date)}
              placeholder="Aucun"
              className={isFieldChanged("commitmentDate") ? "changed" : ""}
            />
          </Form.Item>
          <Form.Item label={<FormLabelWithHelp label="Prochaine recharge" help="Date de la prochaine recharge. Tous les mois, même en annuel." />}>
            <DatePicker
              style={{ ...READ_ONLY_INPUT_STYLE, width: '100%', pointerEvents: 'none', cursor: 'not-allowed' }}
              readOnly
              format="DD/MM/YYYY HH:mm"
              value={wipSubscription.creditsPeriodEnd ? dayjs(wipSubscription.creditsPeriodEnd) : null}
              placeholder="Aucune"
            />
          </Form.Item>
          <Form.Item label={<FormLabelWithHelp label="Jour de recharge" help="Jour du mois où les crédits tombent. Sur un abonnement Stripe il vient du cycle Stripe et ne se règle pas ici. Sinon c'est le début d'abonnement qui le porte, et ce champ est le seul endroit qui le déplace proprement." />}>
            <Flex gap={8}>
              <InputNumber
                style={{ width: '100%' }}
                disabled={isBusy || isReadOnly || isStripeAnchored}
                min={1}
                max={31}
                placeholder="Aucun"
                value={anchorDay ?? currentAnchorDay}
                onChange={(value) => setAnchorDay(value as number | null)}
              />
              <Button
                type="primary"
                disabled={isBusy || isReadOnly || isStripeAnchored || anchorDay === null || anchorDay === currentAnchorDay}
                onClick={confirmAnchorDayChange}
              >
                Déplacer
              </Button>
            </Flex>
          </Form.Item>
          <Form.Item label={<FormLabelWithHelp label="Prochaine facturation" help="Jour où le client est prélevé, porté par la société. En annuel il est à onze mois de la prochaine recharge : ce sont deux dates différentes." />}>
            <DatePicker
              style={{ ...READ_ONLY_INPUT_STYLE, width: '100%', pointerEvents: 'none', cursor: 'not-allowed' }}
              readOnly
              format="DD/MM/YYYY"
              value={billingPeriodEnd ? dayjs(billingPeriodEnd) : null}
              placeholder="Aucune"
            />
          </Form.Item>
        </FormContainer>

        <FormSectionTitle title="Paramètres" />
        <FormContainer>
          <Form.Item label={<Flex align="center"><GithubOutlined style={{ marginRight: 6 }} />Compte bêta</Flex>}>
            <Switch
              disabled={isBusy || isReadOnly}
              checked={wipSubscription.settings?.beta_account}
              onChange={(checked) => {
                formContext.onSubscriptionSettingsBetaAccountChange(checked);
              }}
            />
          </Form.Item>
          <Form.Item label={<Flex align="center"><VideoCameraAddOutlined style={{ marginRight: 6 }} />Compte de démonstration</Flex>}>
            <Switch
              disabled={isBusy || isReadOnly}
              checked={wipSubscription.settings?.demo_account}
              onChange={(checked) => {
                formContext.onSubscriptionSettingsDemoAccountChange(checked);
              }}
            />
          </Form.Item>
          <Form.Item label={<Flex align="center"><CreditCardOutlined style={{ marginRight: 6 }} />Payg</Flex>}>
            <Switch
              disabled={isBusy || isReadOnly}
              checked={wipSubscription.settings?.payg_enabled}
              onChange={(checked) => {
                formContext.onSubscriptionSettingsPaygEnabledChange(checked);
              }}
            />
          </Form.Item>
          <Form.Item label={<Flex align="center"><HourglassOutlined style={{ marginRight: 6 }} />Archivage auto des tâches</Flex>}>
            <Select
              style={{ width: 200 }}
              disabled={isBusy || isReadOnly}
              value={wipSubscription.settings?.tasks_auto_archive_days ?? 0}
              options={[
                { label: 'Jamais', value: 0 },
                { label: '3 jours', value: 3 },
                { label: '7 jours', value: 7 },
                { label: '15 jours', value: 15 },
                { label: '30 jours', value: 30 },
              ]}
              onChange={(value) => {
                formContext.onSubscriptionSettingsAutoArchiveTasksDaysChange(value);
              }}
            />
          </Form.Item>
        </FormContainer>
        <FormContainer twoColumnsMax>
          <Form.Item label={<Flex align="center"><MailOutlined style={{ marginRight: 6 }} />Email en copie des notifications</Flex>}>
            <EmailList
              values={wipSubscription.settings?.email_notification_copy || []}
              onChange={formContext.onSubscriptionSettingsEmailNotificationCopyChange}
              placeholder="Ex: nom.prenom@domaine.com"
              pageSize={5}
            />
          </Form.Item>
        </FormContainer>

        <FormSectionTitle title="Fuseau horaire" />
        <FormContainer>
          <Form.Item label="Ville">
            <Select
              style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
              disabled={isBusy || isReadOnly}
              onChange={(value) =>
                formContext.onTimezoneCityChange(
                  value as SubscriptionTimezoneCity
                )
              }
              value={wipSubscription.timezone.city}
              className={isFieldChanged("timezone.city") ? "changed" : ""}
              options={[
                {
                  label: 'Paris',
                  value: SubscriptionTimezoneCity.paris,
                },
                ...Object.keys(SubscriptionTimezoneCity).filter((key) => !['paris'].includes(key)).map((key) => ({
                  label: subscriptionTimezoneCityAsString(SubscriptionTimezoneCity[key as keyof typeof SubscriptionTimezoneCity]),
                  value: SubscriptionTimezoneCity[key as keyof typeof SubscriptionTimezoneCity],
                })),
              ]}
            />
          </Form.Item>
          <Form.Item label="Continent" style={{ width: '100%' }}>
            <Select
              style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
              disabled={isBusy || isReadOnly}
              onChange={(value) =>
                formContext.onTimezoneContinentChange(
                  value as SubscriptionTimezoneContinent
                )
              }
              value={wipSubscription.timezone.continent}
              className={isFieldChanged("timezone.continent") ? "changed" : ""}
              options={[
                {
                  label: 'Europe',
                  value: SubscriptionTimezoneContinent.europe,
                },
                ...Object.keys(SubscriptionTimezoneContinent).filter((key) => !['europe'].includes(key)).map((key) => ({
                  label: subscriptionTimezoneContinentAsString(SubscriptionTimezoneContinent[key as keyof typeof SubscriptionTimezoneContinent]),
                  value: SubscriptionTimezoneContinent[key as keyof typeof SubscriptionTimezoneContinent],
                })),
              ]}
            >
            </Select>
          </Form.Item>
          <Form.Item label="Pays" style={{ width: '100%' }}>
            <Select
              style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
              disabled={isBusy || isReadOnly}
              onChange={(value) =>
                formContext.onTimezoneCountryChange(
                  value as SubscriptionTimezoneCountry
                )
              }
              value={wipSubscription.timezone.country}
              className={isFieldChanged("timezone.country") ? "changed" : ""}
              options={[
                {
                  label: 'France',
                  value: SubscriptionTimezoneCountry.france,
                }, {
                  label: 'Belgique',
                  value: SubscriptionTimezoneCountry.belgium,
                },
                ...Object.keys(SubscriptionTimezoneCountry).filter((key) => !['france', 'belgium'].includes(key)).map((key) => ({
                  label: subscriptionTimezoneCountryAsString(SubscriptionTimezoneCountry[key as keyof typeof SubscriptionTimezoneCountry]),
                  value: SubscriptionTimezoneCountry[key as keyof typeof SubscriptionTimezoneCountry],
                }))
              ]}
            />
          </Form.Item>
        </FormContainer>

        <FormSectionTitle title="N° d'envoyeur(s)" />
        <FormContainer>
          {numbersContext.sendersStatus === ContextStatus.SUCCESS &&
            wipSubscription.sender.map((sender) => {
              // Match the sender against numbers actually present in our database
              const number = numbersContext.items.find(
                (item) => item.sender === sender
              );

              return (
                <MultiGridInput
                  key={sender}
                  isCreateMode={false}
                  initialValue={
                    number
                      ? `${getSenderCountryFlag(number.sender)} ${number.sender} (${channelAsString(number.channel)}) - ${number.provider}`
                      : `${getSenderCountryFlag(sender)} ${sender}`
                  }
                  hasError={!number}
                  errorTooltip={
                    number
                      ? undefined
                      : "Ce numéro n'existe pas dans la base de données"
                  }
                  onRemove={() => formContext.onSenderRemoved(sender)}
                  onSwap={isReadOnly ? undefined : () => setSenderToMigrate(sender)}
                  swapTooltip="Remplacer ce numéro et migrer tout son historique"
                  disabled={isBusy || isReadOnly}
                  isReadOnly={true}
                  onAction={
                    number
                      ? () => navigate(`${NUMBERS_PAGE_PATH}/${sender}`)
                      : undefined
                  }
                />
              );
            })}
          {isReadOnly === false && (
            <MultiGridInput
              key={'new-sender'}
              isCreateMode={true}
              onCreate={(sender) => {
                // Remove spaces
                sender = sender.replace(/\s/g, '');
                formContext.onSenderAdded(numbersContext.items, sender);
              }}
              disabled={isBusy || isReadOnly}
              isReadOnly={isReadOnly}
              placeholder="N° d'envoyeur"
            />
          )}
        </FormContainer>

        {senderToMigrate !== null && (
          <MigrateSenderModal
            open={true}
            subscriptionId={wipSubscription.id}
            oldSender={senderToMigrate}
            currentRedirection={
              numbersContext.items.find((item) => item.sender === senderToMigrate)?.redirection || ''
            }
            auth={authContext.authentication}
            onSuccess={() => setHasMigratedSender(true)}
            onClose={() => {
              // Lu avant la remise à zéro : sans elle, le drapeau ne retombait que
              // par le rechargement qu'il déclenche lui-même. Toute fermeture
              // suivante sans migration rechargerait la page.
              const bJustMigrated = hasMigratedSender;
              setSenderToMigrate(null);
              setHasMigratedSender(false);
              // Le sender et tout l'historique viennent de bouger côté base : on
              // recharge plutôt que de rafistoler le WIP du formulaire.
              if (bJustMigrated) navigate(0);
            }}
          />
        )}

        {isChangePlanOpen && subPlan !== null && companyId && (
          <ChangePlanModal
            currentPlan={subPlan}
            plans={plansContext.items}
            billingPeriodEnd={billingPeriodEnd ?? null}
            scheduledPlanChange={scheduledPlanChange}
            isBusy={isBusy}
            onClose={() => setIsChangePlanOpen(false)}
            onSubmit={async (newPlanId, scheduledDate) => {
              const done = await formContext.onChangePlan(companyId, newPlanId, scheduledDate);
              if (done) subscriptionsContext.fetchAndSelectById(subscription.id);
              return done;
            }}
          />
        )}

        {creditsModal !== null && (
          <CreditsAdjustmentModal
            mode={creditsModal}
            currentBalance={wipSubscription.credits[ECreditType.leedbucks] ?? 0}
            planAllocation={subPlan?.credits?.leedbucks ?? null}
            isBusy={isBusy}
            onClose={() => setCreditsModal(null)}
            onSubmit={(value, reason, comment) => {
              switch (creditsModal) {
                case 'add':
                  return formContext.onAddCredits(value, reason, comment);
                case 'set':
                  return formContext.onSetCredits(value, reason, comment);
                case 'reset':
                  return formContext.onResetCredits(reason, comment);
              }
            }}
          />
        )}

        <div className="subscription-scenarios-list">
          <FormSectionTitle title={<Flex align="center"><MailOutlined style={{ marginRight: 6 }} />Emails</Flex>} />
          <Popconfirm
            title="Cette action va envoyer un email au client pour l'informer du démarrage de sa facturation auprès de Leedpulse."
            onConfirm={async (event) => {
              if (event) {
                event.stopPropagation();
              }
              formContext.subscriptionStartEmail(
                subscriptionsContext,
                aiContext?.item?.fullname || ''
              );
            }}
            onCancel={(event) => {
              if (event) {
                event.stopPropagation();
              }
            }}
            okText="Confirmer"
            cancelText="Annuler"
          >
            <Button style={{ marginTop: "-30px" }} type="primary" icon={<SendOutlined />}>Email de début de facturation</Button>
          </Popconfirm>
        </div>
      </Form>
    </FormCard>
  );
};

export default SubscriptionDetails;
